import type { SubscriptionPaymentFailure } from "@nlc/shared-types";

import dayjs from "dayjs";

import type { PaymentMethod } from "../../payment-methods/types";

export type PaymentBanner = {
  dismissKey: null | string;
  id: PaymentBannerId;
  message: string;
  severity: "blocking" | "warning";
  title: string;
};

type PaymentBannerId =
  | "card-expired"
  | "card-expires-next-month"
  | "card-expires-soon"
  | `payment-failed-${SubscriptionPaymentFailure["reason"]}`;

const EXPIRES_NEXT_MONTH_DAYS = 30;
const EXPIRES_SOON_DAYS = 90;

export function resolvePaymentBanners({
  defaultMethod,
  paymentFailure,
  today,
}: {
  defaultMethod: null | PaymentMethod;
  paymentFailure: null | SubscriptionPaymentFailure;
  today: Date;
}): PaymentBanner[] {
  const banners: PaymentBanner[] = [];

  if (paymentFailure) banners.push(paymentFailureBanner(paymentFailure));

  if (defaultMethod?.type === "card") {
    const expiryBanner = cardExpiryBanner(defaultMethod, today);

    if (expiryBanner) banners.push(expiryBanner);
  }

  return banners;
}

function paymentFailureBanner({
  paymentMethod,
  reason,
}: SubscriptionPaymentFailure): PaymentBanner {
  const methodNoun = paymentMethod.type === "ach" ? "account" : "card";
  const card = `${brandLabel(paymentMethod.brand)} ${methodNoun} ending in ${paymentMethod.last4}`;
  const id = `payment-failed-${reason}` as const;
  const base = { dismissKey: null, id, severity: "blocking" as const };

  switch (reason) {
    case "declined": {
      return {
        ...base,
        message: `Your bank or card issuer declined the charge to the ${card}. Contact your bank, or update your payment method to keep your plan active.`,
        title: "Your card was declined",
      };
    }
    case "insufficient_funds": {
      return {
        ...base,
        message: `The ${card} did not have enough available balance to cover this charge. Add funds or update your payment method to keep your plan active.`,
        title: "Your payment did not go through",
      };
    }
    case "technical": {
      return {
        ...base,
        message: `We ran into a technical issue charging the ${card}. This is usually temporary. Try again, or update your payment method if it keeps happening.`,
        title: "We could not process your last payment",
      };
    }
  }
}

function cardExpiryBanner(
  card: Extract<PaymentMethod, { type: "card" }>,
  today: Date,
): null | PaymentBanner {
  const expiresAt = dayjs(
    new Date(card.expiryYear, card.expiryMonth - 1),
  ).endOf("month");
  const expiryLabel = expiresAt.format("MMM YYYY");
  const cardLabel = `${brandLabel(card.brand)} ending in ${card.last4} on file`;

  if (dayjs(today).isAfter(expiresAt)) {
    return {
      dismissKey: null,
      id: "card-expired",
      message: `The ${cardLabel} expired in ${expiryLabel}. Update your payment method now to avoid losing access to your plan.`,
      severity: "blocking",
      title: "Your card has expired",
    };
  }

  const daysLeft = expiresAt.diff(today, "day");

  if (daysLeft <= EXPIRES_NEXT_MONTH_DAYS) {
    return {
      dismissKey: `card-expires-next-month:${card.id}`,
      id: "card-expires-next-month",
      message: `The ${cardLabel} expires in ${expiryLabel}. Update your payment method now to keep your plan active without interruption.`,
      severity: "warning",
      title: "Your card expires next month",
    };
  }

  if (daysLeft <= EXPIRES_SOON_DAYS) {
    return {
      dismissKey: `card-expires-soon:${card.id}`,
      id: "card-expires-soon",
      message: `The ${cardLabel} expires in ${expiryLabel}. Update it before then to avoid any interruption to your plan.`,
      severity: "warning",
      title: "Your card expires soon",
    };
  }

  return null;
}

function brandLabel(brand: string) {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}
